import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react'

import { cn } from '@/lib/utils'
import { FieldShell } from './Field'

export interface SelectOption {
  value: string
  label: string
  description?: string
  prefix?: ReactNode
}

interface ComboboxProps {
  options: SelectOption[]
  value: string | null
  onChange: (value: string) => void
  placeholder?: string
  label?: string
  hint?: ReactNode
  error?: string | null
  disabled?: boolean
  searchable?: boolean
  appearance?: 'underline' | 'box'
  required?: boolean
  emptyMessage?: string
  /** The trigger button's id, for a `<label htmlFor>` outside the component. */
  id?: string
}

/**
 * Searchable single-select.
 *
 * Built on a button + listbox rather than a native `<select>` so the country
 * picker can filter as you type and show flags, while keeping keyboard and
 * screen-reader semantics.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Select…',
  label,
  hint,
  error,
  disabled,
  searchable = true,
  appearance = 'underline',
  required,
  emptyMessage = 'No matches',
  id,
}: ComboboxProps) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listId = useId()

  const selected = options.find((option) => option.value === value) ?? null

  const filtered = useMemo(() => {
    if (!query.trim()) return options
    const needle = query.toLowerCase()
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        option.value.toLowerCase().includes(needle),
    )
  }, [options, query])

  // Close when focus or a click lands outside the component.
  useEffect(() => {
    if (!open) return
    const handlePointer = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handlePointer)
    return () => document.removeEventListener('mousedown', handlePointer)
  }, [open])

  useEffect(() => {
    if (open && searchable) inputRef.current?.focus()
    if (!open) {
      setQuery('')
      setActiveIndex(0)
    }
  }, [open, searchable])

  const commit = (option: SelectOption) => {
    onChange(option.value)
    setOpen(false)
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (!open && (event.key === 'ArrowDown' || event.key === 'Enter')) {
      event.preventDefault()
      setOpen(true)
      return
    }
    if (!open) return

    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, filtered.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Enter') {
      event.preventDefault()
      const option = filtered[activeIndex]
      if (option) commit(option)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    }
  }

  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      <div ref={containerRef} className="relative">
        <button
          id={id}
          type="button"
          disabled={disabled}
          onClick={() => setOpen((isOpen) => !isOpen)}
          onKeyDown={handleKeyDown}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          className={cn(
            'flex w-full items-center justify-between text-left transition-colors',
            appearance === 'underline'
              ? 'border-0 border-b border-line bg-transparent py-3 text-lg'
              : 'rounded-field border border-line bg-surface px-3.5 py-2.5 text-[15px]',
            open && (appearance === 'underline' ? 'border-ink' : 'border-ink-soft'),
            error && 'border-danger',
            disabled && 'cursor-not-allowed opacity-55',
          )}
        >
          <span className={cn('flex items-center gap-2 truncate', !selected && 'text-ink-faint')}>
            {selected?.prefix}
            {selected?.label ?? placeholder}
          </span>
          <Chevron open={open} />
        </button>

        {open && (
          <div className="absolute z-40 mt-1.5 w-full overflow-hidden rounded-field border border-line bg-surface shadow-float animate-fade-in">
            {searchable && (
              <div className="border-b border-line p-2">
                <input
                  ref={inputRef}
                  value={query}
                  onChange={(event) => {
                    setQuery(event.target.value)
                    setActiveIndex(0)
                  }}
                  onKeyDown={handleKeyDown}
                  placeholder="Search…"
                  className="w-full rounded-[7px] bg-surface-muted px-2.5 py-1.5 text-sm outline-none placeholder:text-ink-faint"
                />
              </div>
            )}
            <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto py-1">
              {filtered.length === 0 && (
                <li className="px-3 py-2.5 text-sm text-ink-muted">{emptyMessage}</li>
              )}
              {filtered.map((option, index) => (
                <li key={option.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => commit(option)}
                    className={cn(
                      'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                      index === activeIndex && 'bg-surface-muted',
                      option.value === value && 'font-medium text-ink',
                    )}
                  >
                    {option.prefix}
                    <span className="flex-1 truncate">{option.label}</span>
                    {option.value === value && <Check />}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </FieldShell>
  )
}

interface MultiSelectProps extends Omit<ComboboxProps, 'value' | 'onChange'> {
  values: string[]
  onChange: (values: string[]) => void
  maxItems?: number
}

/** Multi-select rendering chosen items as removable chips. */
export function MultiSelect({
  options,
  values,
  onChange,
  placeholder = 'Search and add…',
  label,
  hint,
  error,
  appearance = 'box',
  required,
  maxItems,
}: MultiSelectProps) {
  const available = options.filter((option) => !values.includes(option.value))
  const atLimit = maxItems !== undefined && values.length >= maxItems

  return (
    <FieldShell label={label} hint={hint} error={error} required={required}>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5',
          appearance === 'box'
            ? 'min-h-[46px] rounded-field border border-line bg-surface px-2.5 py-2'
            : 'border-b border-line py-2.5',
          error && 'border-danger',
        )}
      >
        {values.map((value) => {
          const option = options.find((o) => o.value === value)
          return (
            <span
              key={value}
              className="inline-flex items-center gap-1.5 rounded-[7px] bg-accent-soft px-2 py-1 text-[13px] text-ink"
            >
              {option?.prefix}
              {option?.label ?? value}
              <button
                type="button"
                onClick={() => onChange(values.filter((v) => v !== value))}
                aria-label={`Remove ${option?.label ?? value}`}
                className="text-ink-muted transition-colors hover:text-ink"
              >
                ×
              </button>
            </span>
          )
        })}
        <div className="min-w-[160px] flex-1">
          <Combobox
            options={available}
            value={null}
            onChange={(value) => !atLimit && onChange([...values, value])}
            placeholder={atLimit ? `Limit of ${maxItems} reached` : placeholder}
            appearance="underline"
            disabled={atLimit}
            emptyMessage="Nothing left to add"
          />
        </div>
      </div>
    </FieldShell>
  )
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={cn('h-4 w-4 shrink-0 text-ink-muted transition-transform', open && 'rotate-180')}
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path d="m5 7.5 5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function Check() {
  return (
    <svg className="h-4 w-4 text-ink" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="m4 10.5 4 4 8-9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  )
}
